import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { Logo } from "@gately/ui/logo";

import { BorneForm } from "~/components/borne/borne-form";
import { phoneFormApi, phonePageState } from "~/lib/borne/phone-api";
import { createPhoneBorneClient } from "~/lib/borne/phone-client";

export const Route = createFileRoute("/b/$token")({
  component: PhoneBornePage,
});

const EXPIRED_COPY = "Ce code a expiré, scannez à nouveau l'écran.";
const DESK_COPY = "Présentez-vous à l'accueil.";

/** `openForm` is public and needs no credential: a token-less client. */
const openerClient = createPhoneBorneClient(() => null);

/**
 * The phone side of the borne (spec §3). The QR token in the URL is swapped
 * once for a 15-minute form token, which lives in memory only — the query
 * cache and this component's render — never in localStorage: it is a bearer
 * credential on someone else's phone, and a reload simply scans again.
 */
function PhoneBornePage() {
  const { token } = Route.useParams();
  const [expired, setExpired] = useState(false);
  const [done, setDone] = useState(false);
  const [consumed, setConsumed] = useState(false);

  const open = useQuery({
    queryKey: ["borne", "openForm", token],
    queryFn: () => openerClient.borne.openForm({ qrToken: token }),
    // One exchange per page load: every call spends the per-IP budget and
    // mints a new nonce.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // One fiche per token: once the server accepts (or rejects the token), the
  // client stops sending it.
  const formToken = expired || consumed ? null : (open.data?.formToken ?? null);
  const opened = open.data;

  const api = useMemo(() => {
    if (!opened) return null;
    const client = createPhoneBorneClient(() => formToken);
    return phoneFormApi(client.borne, {
      orgName: opened.orgName,
      idPolicy: opened.idPolicy,
      onExpired: () => setExpired(true),
      onSubmitted: () => setConsumed(true),
    });
  }, [opened, formToken]);

  // BorneForm wants a stable onDone (it is an effect dependency there).
  const finish = useCallback(() => setDone(true), []);

  const state = phonePageState({
    isPending: open.isPending,
    error: open.error,
    expired,
    done,
  });

  if (state === "form" && api) {
    // Same dark station surface the /borne layout gives the tablet: the form
    // is styled for it (white text), and this route sits outside that layout.
    return (
      <div className="station bg-background text-foreground min-h-dvh">
        <BorneForm api={api} kiosk={false} onDone={finish} />
      </div>
    );
  }

  return (
    <main className="flex min-h-dvh flex-col items-center bg-neutral-950 px-6 py-10 text-white">
      <Logo variant="light" withWordmark />
      <div className="mt-24 flex flex-col items-center text-center">
        {state === "loading" ? (
          <Notice body="Chargement…" />
        ) : state === "expired" ? (
          <Notice body={EXPIRED_COPY} />
        ) : state === "offline" ? (
          <Notice body="Connexion impossible.">
            <Button className="mt-6 h-12" onClick={() => void open.refetch()}>
              Réessayer
            </Button>
          </Notice>
        ) : (
          <Notice body={DESK_COPY} />
        )}
      </div>
    </main>
  );
}

function Notice({ body, children }: { body: string; children?: ReactNode }) {
  return (
    <>
      <p className="text-xl font-medium">{body}</p>
      {children}
    </>
  );
}
